import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import '../styles/LearningPage.css';

const LearningPage = () => {
  const { courseId, lessonId } = useParams();
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  
  // 인증 확인
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);
  
  // 강의 및 레슨 데이터 가져오기
  useEffect(() => {
    const fetchCourseData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // 실제 API 호출 대신 임시 데이터 사용
        // const courseRes = await api.get(`/courses/${courseId}`);
        // const lessonsRes = await api.get(`/courses/${courseId}/lessons`);
        
        // 임시 데이터
        setTimeout(() => {
          const mockCourse = {
            _id: courseId,
            title: 'EJU 물리 종합 과정',
            description: '일본 유학시험(EJU)을 준비하는 학생들을 위한 종합 물리 과정입니다. 역학, 전자기학, 열역학 등 주요 영역을 집중적으로 다루며, 실전 문제 풀이와 해설을 통해 시험에 완벽하게 대비할 수 있습니다.',
            instructor: {
              name: '박민준',
              bio: '한일이공계학부유학 국비유학생으로 선발되어 홋카이도 대학 정보이공학 전공 후 일반입시로 삿포로의과대학에 합격',
              avatar: 'https://via.placeholder.com/100x100?text=박민준'
            }
          };
          
          const mockLessons = [
            {
              _id: 'lesson1',
              title: '1강: EJU 물리 시험 소개 및 학습 전략',
              description: 'EJU 물리 시험의 구성과 특징, 효과적인 학습 방법에 대해 알아봅니다.',
              videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
              duration: '45분',
              materials: [
                { name: '1강 강의 자료', url: '#' },
                { name: '학습 계획표', url: '#' }
              ],
              isCompleted: true
            },
            {
              _id: 'lesson2',
              title: '2강: 역학 기초 - 운동학과 뉴턴의 법칙',
              description: '물체의 운동을 기술하는 기본 개념과 뉴턴의 운동 법칙에 대해 학습합니다.',
              videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
              duration: '50분',
              materials: [
                { name: '2강 강의 자료', url: '#' },
                { name: '연습 문제', url: '#' }
              ],
              isCompleted: false
            },
            {
              _id: 'lesson3',
              title: '3강: 역학 응용 - 일과 에너지',
              description: '일, 에너지, 운동량 보존 법칙 등 역학의 핵심 개념을 심화 학습합니다.',
              videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
              duration: '55분',
              materials: [
                { name: '3강 강의 자료', url: '#' },
                { name: '심화 문제', url: '#' }
              ],
              isCompleted: false
            },
            {
              _id: 'lesson4',
              title: '4강: 전자기학 기초 - 전기장과 자기장',
              description: '전기장과 자기장의 개념, 전하와 전류의 상호작용에 대해 학습합니다.',
              videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
              duration: '60분',
              materials: [
                { name: '4강 강의 자료', url: '#' },
                { name: '연습 문제', url: '#' }
              ],
              isCompleted: false
            },
            {
              _id: 'lesson5',
              title: '5강: 열역학 기초 - 열과 온도',
              description: '열역학의 기본 법칙과 엔트로피 개념에 대해 학습합니다.',
              videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
              duration: '65분',
              materials: [
                { name: '5강 강의 자료', url: '#' },
                { name: '연습 문제', url: '#' }
              ],
              isCompleted: false
            }
          ];
          
          setCourse(mockCourse);
          setLessons(mockLessons);
          
          // 현재 레슨 설정
          const initialLessonId = lessonId || mockLessons[0]._id;
          const currentLessonData = mockLessons.find(lesson => lesson._id === initialLessonId);
          setCurrentLesson(currentLessonData);
          
          // 진행률 계산
          const completedCount = mockLessons.filter(lesson => lesson.isCompleted).length;
          setProgress(Math.round((completedCount / mockLessons.length) * 100));
          
          setIsLoading(false);
        }, 1000);
      } catch (err) {
        console.error('강의 데이터를 불러오는데 실패했습니다:', err);
        setError('강의 데이터를 불러오는데 실패했습니다.');
        setIsLoading(false);
      }
    };
    
    if (isAuthenticated && courseId) {
      fetchCourseData();
    }
  }, [courseId, lessonId, isAuthenticated]);
  
  // 레슨 변경 핸들러
  const handleLessonChange = (lesson) => {
    setCurrentLesson(lesson);
    navigate(`/courses/${courseId}/learn/${lesson._id}`);
    window.scrollTo(0, 0);
  };
  
  // 레슨 완료 핸들러
  const handleMarkComplete = async () => {
    try {
      // 실제 API 호출 대신 임시 처리
      // await api.post(`/courses/${courseId}/lessons/${currentLesson._id}/complete`);
      
      // 임시 처리
      const updatedLessons = lessons.map(lesson => 
        lesson._id === currentLesson._id 
          ? { ...lesson, isCompleted: true } 
          : lesson
      );
      
      setLessons(updatedLessons);
      setCurrentLesson({ ...currentLesson, isCompleted: true });
      
      // 진행률 업데이트
      const completedCount = updatedLessons.filter(lesson => lesson.isCompleted).length;
      setProgress(Math.round((completedCount / updatedLessons.length) * 100));
    } catch (err) {
      console.error('레슨 완료 처리 중 오류가 발생했습니다:', err);
    }
  };
  
  // 노트 저장 핸들러
  const handleSaveNotes = async () => {
    try {
      // 실제 API 호출 대신 임시 처리
      // await api.post(`/courses/${courseId}/lessons/${currentLesson._id}/notes`, { content: notes });
      alert('노트가 저장되었습니다.');
    } catch (err) {
      console.error('노트 저장 중 오류가 발생했습니다:', err);
    }
  };
  
  if (loading || isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>강의를 불러오는 중...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-primary">
          다시 시도
        </button>
      </div>
    );
  }
  
  if (!course || !currentLesson) {
    return (
      <div className="error-container">
        <p>강의를 찾을 수 없습니다.</p>
        <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
          대시보드로 돌아가기
        </button>
      </div>
    );
  }
  
  return (
    <div className="learning-page">
      <div className="service-notice" style={{ margin: '1rem 1.25rem 0' }}>
        <strong>아카이브 학습실</strong>
        <span>신규 수강·결제는 중단되었습니다. 아래는 이전 기록 조회용입니다.</span>
      </div>
      {/* 강의 헤더 */}
      <div className="course-header">
        <div className="course-info">
          <h1>{course.title}</h1>
          <p className="instructor">구성: {course.instructor.name}</p>
        </div>
        <div className="course-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <span className="progress-text">{progress}% 완료</span>
        </div>
      </div>
      
      {/* 학습 콘텐츠 */}
      <div className="learning-content">
        {/* 레슨 목록 사이드바 */}
        <div className="lessons-sidebar">
          <h2>강의 목차</h2>
          <ul className="lessons-list">
            {lessons.map(lesson => (
              <li 
                key={lesson._id} 
                className={`lesson-item ${currentLesson._id === lesson._id ? 'active' : ''} ${lesson.isCompleted ? 'completed' : ''}`}
                onClick={() => handleLessonChange(lesson)}
              >
                <span className="lesson-title">{lesson.title}</span>
                <span className="lesson-duration">{lesson.duration}</span>
                {lesson.isCompleted && <span className="completion-badge">✓</span>}
              </li>
            ))}
          </ul>
        </div>
        
        {/* 현재 레슨 콘텐츠 */}
        <div className="lesson-content">
          <div className="lesson-header">
            <h2>{currentLesson.title}</h2>
            <p className="lesson-description">{currentLesson.description}</p>
          </div>
          
          {/* 비디오 플레이어 */}
          <div className="video-container">
            <iframe 
              src={currentLesson.videoUrl} 
              title={currentLesson.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          
          {/* 학습 자료 */}
          <div className="lesson-materials">
            <h3>학습 자료</h3>
            <ul className="materials-list">
              {currentLesson.materials.map((material, index) => (
                <li key={index}>
                  <a href={material.url} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-file-pdf"></i> {material.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* 노트 섹션 */}
          <div className="notes-section">
            <div className="notes-header" onClick={() => setShowNotes(!showNotes)}>
              <h3>내 학습 노트</h3>
              <button className="toggle-notes">
                {showNotes ? '접기' : '펼치기'}
              </button>
            </div>
            
            {showNotes && (
              <div className="notes-content">
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="이 강의에 대한 노트를 작성하세요..."
                  rows="6"
                ></textarea>
                <button className="btn btn-secondary" onClick={handleSaveNotes}>
                  노트 저장
                </button>
              </div>
            )}
          </div>
          
          {/* 레슨 완료 버튼 */}
          <div className="lesson-actions">
            {!currentLesson.isCompleted ? (
              <button 
                className="btn btn-primary btn-complete"
                onClick={handleMarkComplete}
              >
                이 강의 완료하기
              </button>
            ) : (
              <button className="btn btn-secondary btn-completed" disabled>
                완료됨
              </button>
            )}
            
            {/* 다음 레슨 버튼 */}
            {lessons.findIndex(l => l._id === currentLesson._id) < lessons.length - 1 && (
              <button 
                className="btn btn-outline btn-next"
                onClick={() => {
                  const currentIndex = lessons.findIndex(l => l._id === currentLesson._id);
                  if (currentIndex < lessons.length - 1) {
                    handleLessonChange(lessons[currentIndex + 1]);
                  }
                }}
              >
                다음 강의로 이동
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPage; 